import { FC, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';

import {
    BackLink,
    BodyText,
    Button,
    Card,
    Headline,
    Layout,
    Page,
    Spinner,
    Stack,
    Tab,
    TabGroup,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';

import { DeveloperType } from '../../api/app.api';
import { AppListPageStore } from '../stores/app-list-page.store';
import { AppPurpose, ThirdPartyGateDialog } from './third-party-gate-dialog';
import { AppSummaryTable } from './app-summary-table';
import { FolderAddIcon } from './icons';

export const AppListPage: FC = provide({ singletons: [AppListPageStore] })(
    observer(() => {
        const [store] = useDependencies(AppListPageStore);
        const history = useHistory();
        const { search } = useLocation();
        const deleted = new URLSearchParams(search).has('deleted');

        useEffect(() => {
            store.load(deleted);
        }, [store, deleted]);

        const goToHomePage = useCallback(() => {
            history.push('/');
        }, [history]);

        const handleCreateNewApp = useCallback(() => {
            if (store.developerType === DeveloperType.Customer) {
                store.showThirdPartyGateDialog();
            } else {
                history.push('/apps/new');
            }
        }, [store, history]);

        return (
            <Page
                header={
                    <Stack direction="column">
                        <Stack.Item>
                            <BackLink pageLabel="Home" onClick={goToHomePage} />
                        </Stack.Item>
                        <Stack.Item>
                            <Headline size="large">Register and Manage Applications</Headline>
                        </Stack.Item>
                    </Stack>
                }
            >
                <Layout>
                    <Stack direction="row" justifyContent="space-between">
                        <Headline>My Apps</Headline>
                        <Button primary disabled={store.loading} onClick={handleCreateNewApp}>
                            Register New App
                        </Button>
                    </Stack>
                    <TabGroup>
                        <Tab
                            active={!deleted}
                            onClick={() => {
                                history.push('/apps');
                            }}
                        >
                            Current Apps
                        </Tab>
                        <Tab
                            active={deleted}
                            onClick={() => {
                                history.push('/apps?deleted');
                            }}
                        >
                            Deleted Apps
                        </Tab>
                    </TabGroup>
                    {store.loading ? (
                        <Stack direction="column" alignItems="center" className="m-t-5">
                            <Spinner size="large" />
                        </Stack>
                    ) : store.appGridItems.length > 0 || deleted ? (
                        <AppSummaryTable
                            appGridItems={store.appGridItems}
                            showApiCategories={store.showApiCategories}
                            showReviewStatus={store.showReviewStatus}
                            noRecordsText={deleted ? 'There are no deleted apps.' : undefined}
                        />
                    ) : (
                        <Card>
                            <Stack direction="column" alignItems="center" spacing={3}>
                                {FolderAddIcon}
                                <BodyText subdued>
                                    It looks like you haven’t registered any apps yet.
                                </BodyText>
                                <Button
                                    primary
                                    fill="outline"
                                    disabled={store.loading}
                                    onClick={handleCreateNewApp}
                                >
                                    Register New App
                                </Button>
                            </Stack>
                        </Card>
                    )}
                </Layout>
                {store.thirdPartyGateDialogVisible && (
                    <ThirdPartyGateDialog
                        onContinue={async (appPurpose: AppPurpose) => {
                            await store.handleThirdPartyGateDialogContinue(
                                appPurpose === AppPurpose.OwnOrg,
                            );
                            history.push(
                                appPurpose === AppPurpose.OwnOrg
                                    ? '/apps/new'
                                    : '/apps/request-new',
                            );
                        }}
                        onCancel={() => {
                            store.hideThirdPartyGateDialog();
                        }}
                    />
                )}
            </Page>
        );
    }),
);
AppListPage.displayName = 'AppListPage';
